import React from 'react';
import { Box, Text } from '../../pdf-design-system';
import { useTraits } from './Traits';

/**
 *
 * @param {object} props
 * @param {string | JSX.Element} [props.label] The label for the trait.
 * @param {string | React.ReactNode} props.children The value of the trait, including actual rendering.
 * @param {boolean} [props.hide] If false, the trait renders the EmptyValue component instead.
 * @param {boolean} [props.debug] Shows debug boxes for item, value and label.
 * @param {number} [props.span] If used within a Columns component, changes the number of columns to span.
 * @param {number} [props.width] If used within a Columns component, changes the width of the component.
 * @param {boolean} [props.hide] If used within a Columns component, removes the Trait from the columns completely.
 * @param {import('@react-pdf/types').Style} [props.style] Custom styling for this component.
 * @param {any} [props.emptyValue] This is used as the children if the value is empty.
 * @returns
 */

export default function Trait({
  label,
  children,
  emptyValue = children,
  debug,
  ...rest
}) {
  return (
    <Trait.Wrapper debug={debug} {...rest}>
      <Trait.Label debug={debug}>{label}</Trait.Label>
      <Trait.Value debug={debug}>{children}</Trait.Value>
    </Trait.Wrapper>
  );
}

/**
 *
 * @param {object} props
 * @param {string | React.ReactNode} props.children The contents of the trait.
 * @param {boolean} [props.debug] Shows a debug box.
 * @param {import('@react-pdf/types').Style} [props.style] Custom styling for this component.
 * @returns
 */
function TraitWrapper({ children, style, ...rest }) {
  const { styles } = useTraits();
  return (
    <Box style={[...styles('item'), style]} {...rest}>
      {children}
    </Box>
  );
}
Trait.Wrapper = TraitWrapper;

/**
 *
 * @param {object} props
 * @param {string | React.ReactNode} [props.children] The label of the trait.
 * @param {boolean} [props.debug] Shows a debug box.
 * @param {import('@react-pdf/types').Style} [props.style] Custom styling for this component.
 * @returns
 */
function TraitLabel({ children, style, ...rest }) {
  const { styles } = useTraits();
  return (
    <Text style={[...styles('label'), style]} {...rest}>
      {children}
    </Text>
  );
}
Trait.Label = TraitLabel;

/**
 *
 * @param {object} props
 * @param {string | React.ReactNode} [props.children] The value of the trait, including actual rendering.
 * @param {boolean} [props.debug] Shows a debug box.
 * @param {import('@react-pdf/types').Style} [props.style] Custom styling for this component.
 * @param {import('./Traits').TraitElement} [props.variant] Shows a debug box.
 * @returns
 */
function TraitValue({ children, style, variant = 'value', ...rest }) {
  const { styles } = useTraits();
  return (
    <Text style={[...styles(variant), style]} {...rest}>
      {children}
    </Text>
  );
}
Trait.Value = TraitValue;
